import { SetStateAction, useEffect, useState } from "react";
import { GridMetaData } from "models/grids";
import { useCurrentUser, useCentralApi } from "hooks/shared";
import { WorkItemStatusesForResourceUpdate } from "components/bulk-update/fields/WorkItemResourceUpdateSearchFieldStatus";
import { WorkItemSearchCriteria } from "hooks/work-items/useWorkItem";

export interface GetWorkItemWithResourcesResult {
    workItems: WorkItemWithResources[]
}

export interface WorkItemWithResources {
    id: string
    workItemInstanceId: number
    parentWorkItemInstanceId?: number
    treePath: string
    treeLevel: number
    workItemInstanceName: string
    workItemTemplateId: number
    workItemTemplateName: string
    workspaceInstanceId: number
    workspaceInstanceName: string
    workItemStatusId: number
    workItemStatusName: string
    versionStamp: string
    selected: boolean
    error: boolean
    errorMessage: any
    selectedResponsibility: any
    isDirty: boolean
    parentTreePath: string
    contentTypeId?: number
    contentTypeName?: string
    responsibilitiesResources: ResponsibilityWithResources[]
    selectedResponsibilityId: number;
    rowChanged: boolean;
    originalSelectedValue? : number
    newResponsibilitiesResources?: ResponsibilityWithResources[],
    responsibilityId: number,
    responsibilityName: string,
    resource: AssignedUser,
    primaryResource:AssignedUser
}
export interface WorkItemResourceSearchCriteria extends WorkItemSearchCriteria {
    responsibilities: number[];
    responsibilityUseOrOperator: boolean;
}

export interface ResponsibilityWithResources {
    responsibilityId: number;
    responsibilityName: string;
    resources: AssignedUser[];
}

export interface AssignedUser {
    isDefaultAssignee: boolean;
    isNew: boolean;
    selected: boolean;
    userId: string;
    userName: string;
    versionStamp?: string;
    isAssignOnInProgress: boolean;
}

type SearchResult = [
    GetWorkItemWithResourcesResult | null,
    boolean,
    string | null,
    React.Dispatch<SetStateAction<GetWorkItemWithResourcesResult | null>>
];

const preprocessDataForUI = (data: WorkItemWithResources[], searchCriteria: WorkItemSearchCriteria) => {
    var updatedWorkItems = data.map((wiObj: WorkItemWithResources) => {
        //initialize selected responsibility to the first responsibility
        /* WILL BE CLEANED UP IN THE BFLY-12189.
        var res = { ...wiObj, id: wiObj.treePath, selectedResponsibilityId: getSelectedResponsibilityIds(wiObj, searchCriteria), rowChanged: false };
        res.responsibilitiesResources.forEach((respObj: any) => {
            respObj.resources.forEach((resource: any) => {
                resource.selected = resource.isDefaultAssignee;
                resource.isAssignOnInProgress = false;
            });
        });
        */
        var res = { ...wiObj, id: wiObj.treePath, rowChanged: false };
        return res;
    });

    return ({ workItems: updatedWorkItems });
}

const getSelectedResponsibilityIds = (workItem: WorkItemWithResources, searchCriteria: WorkItemSearchCriteria) => {
    if (searchCriteria.responsibilities.length === 0) {
        if (searchCriteria.userId && searchCriteria.matchOnPrimary) {
            const filteredResponsibilities = workItem.responsibilitiesResources.filter((resp: ResponsibilityWithResources) => !!resp.resources.find(x => x.userId === searchCriteria.userId && x.isDefaultAssignee));
            return filteredResponsibilities?.length ? filteredResponsibilities.sort((a: ResponsibilityWithResources, b: ResponsibilityWithResources) => a.responsibilityName.toLowerCase() > b.responsibilityName.toLowerCase() ? 1 : -1)[0].responsibilityId : workItem.responsibilitiesResources[0].responsibilityId;
        } else {
            return workItem.responsibilitiesResources[0].responsibilityId;
        }
    }
    else if (workItem.responsibilitiesResources?.length) {
        const filteredResponsibilities = workItem.responsibilitiesResources.filter((resp: any) => searchCriteria.responsibilities.includes(resp.responsibilityId));
        return filteredResponsibilities?.length ? filteredResponsibilities.sort((a: ResponsibilityWithResources, b: ResponsibilityWithResources) => a.responsibilityName.toLowerCase() > b.responsibilityName.toLowerCase() ? 1 : -1)[0].responsibilityId : 0;
    }
    return 0;
}

const useWorkItemsWithResources: (searchCriteria: WorkItemSearchCriteria, gridMetadata: GridMetaData, doSearch: boolean, setDoSearch: React.Dispatch<SetStateAction<boolean>>) => SearchResult = (searchCriteria, gridMetadata, doSearch, setDoSearch) => {
    const [result, setResult] = useState<GetWorkItemWithResourcesResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    useEffect(() => {
        const controller = new AbortController();
        const fetchTasks = async () => {
            if (user && user.id_token && doSearch) {
                setLoading(true);
                setResult(null);
                try {
                    searchCriteria.status = searchCriteria?.status?.length > 0 ? searchCriteria.status : WorkItemStatusesForResourceUpdate.map(status => +status.id);
                    const resp = await fetcher(`/api/v3/bulkupdate/workitem/SearchWorkItemsResponsibilitiesResources`, {
                        method: 'POST',
                        body: JSON.stringify(searchCriteria),
                        signal: controller.signal
                    });

                    if (!resp.ok) {
                        // RESPOND TO ERRORS
                    }

                    const res = await resp.json();
                    const uiFriendlyData = preprocessDataForUI(res.workItems, searchCriteria);
                    const data = JSON.parse(JSON.stringify(uiFriendlyData));

                    setResult(data);

                } catch (err) {
                    // TODO: 
                } finally {
                    setLoading(false);
                    if (doSearch)
                        setDoSearch(false);
                }
            }
            else if (result === null && !doSearch) {
                setLoading(true);
                setResult({
                    workItems: []
                });
                setLoading(false);
            }
        };
        fetchTasks()
            .catch((err) => { });

        return () => {
            if (!doSearch) {
                controller.abort();
                setLoading(false);
            }
        };
    }, [doSearch]);

    return [
        result,
        loading,
        error,
        setResult
    ];
};

export default useWorkItemsWithResources;